/**
 * Hook for managing plugin builder sessions list (CRUD).
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import type { PluginBuilderSession } from '../types';

interface UsePluginBuilderSessionsReturn {
  sessions: PluginBuilderSession[];
  isLoading: boolean;
  isCreating: boolean;
  error: Error | null;
  createSession: (params?: { name?: string }) => Promise<string | null>;
  deleteSession: (sessionId: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

export function usePluginBuilderSessions(): UsePluginBuilderSessionsReturn {
  const [sessions, setSessions] = useState<PluginBuilderSession[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const navigate = useNavigate();

  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.listPluginBuilderSessions();
      setSessions(response.sessions as PluginBuilderSession[]);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch sessions');
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const createSession = useCallback(
    async (params?: { name?: string }): Promise<string | null> => {
      setIsCreating(true);
      setError(null);

      try {
        const response = await api.createPluginBuilderSession(params);
        navigate(`/a/arche_plugin_builder/${response.sessionId}`);
        return response.sessionId;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to create session');
        setError(error);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    [navigate],
  );

  const deleteSession = useCallback(async (sessionId: string): Promise<boolean> => {
    setError(null);

    try {
      await api.deletePluginBuilderSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
      return true;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete session');
      setError(error);
      return false;
    }
  }, []);

  return {
    sessions,
    isLoading,
    isCreating,
    error,
    createSession,
    deleteSession,
    refresh: fetchSessions,
  };
}
